import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Router } from '@angular/router';
import { ChatService, ListChatItem, AdminService } from 'ldt-chat-api';
import { AuthService } from 'src/app/auth/service/auth.service';
import { OrgsService } from 'src/app/shared/orgs/orgs.service';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { NotificationService } from '../../notification-service/notification.service';
import { Log } from 'src/app/logger.service';
import { PeopleChatService } from 'src/app/core/services/people-chat.service';
import { LangGraphInstanceService } from 'src/app/core/services/langgraph-instance.service';

// Extended interface to track per-item feedback loading state.
// `feedback` itself is already part of ListChatItem.
interface ListChatItemWithFeedback extends ListChatItem {
  feedbackLoading?: boolean;
}

@Component({
  selector: 'app-chat-history-drawer',
  templateUrl: './chat-history-drawer.component.html',
  styleUrls: ['./chat-history-drawer.component.scss'],
  standalone: false,
})
export class ChatHistoryDrawerComponent {
  @Input() orgId: string;
  @Input() isOpen: boolean = false;
  @Output() closeDrawer = new EventEmitter<void>();

  chatHistory: ListChatItemWithFeedback[] = [];
  chatsRefreshing: boolean = false;
  showAllChats: boolean = false;
  isAdmin: Observable<boolean>;

  constructor(
    private chatService: ChatService,
    private peopleChatService: PeopleChatService,
    private adminService: AdminService,
    private authService: AuthService,
    private router: Router,
    public orgsService: OrgsService,
    private notify: NotificationService,
    private langGraphInstance: LangGraphInstanceService
  ) {}

  ngOnInit() {
    this.isAdmin = this.authService.$isAdmin;
  }

  ngOnChanges() {
    // Fetch chats when the drawer is opened or org changes
    if (this.isOpen) {
      this.getChats();
    }
  }

  getChats(refresh = false) {
    if (this.chatsRefreshing) {
      return;
    }

    this.chatsRefreshing = true;

    if (this.showAllChats) {
      this.adminService.listAllChats(this.langGraphInstance.selectedInstanceValue ?? undefined, undefined, 100).subscribe({
        next: (data: any) => {
          // Sort once so the displayed list and the index-based feedback
          // updates below stay aligned.
          const chats = this.sortByMostRecent(data.chats || []);

          // Set chatHistory immediately so UI updates fast
          // Mark as loading feedback for admin mode
          this.chatHistory = chats.map((chat: ListChatItem) => ({
            ...chat,
            feedback: [],
            feedbackLoading: true,
          }));
          this.chatsRefreshing = false;

          // Fetch feedback for each chat asynchronously
          this.fetchFeedbackForChats(chats);
        },
        error: (err: any) => {
          console.error('Error getting chats:', err);
          this.chatsRefreshing = false;
        },
      });
    } else {
      this.peopleChatService.listThreads(this.orgId, refresh).subscribe({
        next: (chats: ListChatItem[]) => {
          // Set chatHistory immediately so UI updates fast
          this.chatHistory = this.sortByMostRecent(chats).map((chat: ListChatItem) => ({
            ...chat,
            feedback: [],
            feedbackLoading: false,
          }));
          this.chatsRefreshing = false;
        },
        error: (err: any) => {
          console.error('Error getting chats:', err);
          this.chatsRefreshing = false;
        },
      });
    }
  }

  private fetchFeedbackForChats(chats: ListChatItem[]) {
    // Fetch feedback for each chat individually and update as responses come back
    chats.forEach((chat, index) => {
      if (!chat.id || !chat.org_id) {
        // Mark as not loading if we're skipping it
        if (this.chatHistory[index]?.id === chat.id) {
          this.chatHistory[index] = {
            ...this.chatHistory[index],
            feedbackLoading: false,
          };
        }
        return;
      }

      this.chatService
        .listFeedbackForChat(chat.org_id, chat.id)
        .pipe(
          catchError((err) => {
            this.notify.error(`Error fetching feedback for chat ${chat.id}:`, err);
            Log.s(`Error fetching feedback for chat ${chat.id}:`, 'error', { error: err });
            return of({ feedback: [] });
          })
        )
        .subscribe((response) => {
          // Update the specific chat item with feedback data and mark as loaded
          if (this.chatHistory[index]?.id === chat.id) {
            this.chatHistory[index] = {
              ...this.chatHistory[index],
              feedback: response.feedback || [],
              feedbackLoading: false,
            };
          }
        });
    });
  }

  toggleAdminMode() {
    this.showAllChats = !this.showAllChats;
    this.getChats(true); // Force refresh when switching modes
  }

  onRowClicked(event: any) {
    const id = event.data.id;
    // Navigate to chat with threadId, preserving current route structure
    this.router.navigate([], {
      queryParams: { threadId: id },
      queryParamsHandling: 'merge',
      relativeTo: this.router.routerState.root,
    });
    this.closeDrawer.emit();
  }

  getMessageCount(chat: ListChatItem) {
    // The list payload only includes user/assistant messages (no intermediate
    // "thinking" entries), so every message in the array counts.
    return chat.messages?.length || 0;
  }

  isDeepResearch(chat: ListChatItem): boolean {
    return chat.mode === ListChatItem.ModeEnum.DeepResearch;
  }

  // Most-recent activity first. The API doesn't guarantee an order, so sort
  // client-side by updated_at (falling back to created_at) to match the date
  // shown on each row.
  private sortByMostRecent<T extends ListChatItem>(chats: T[]): T[] {
    return [...chats].sort((a, b) => this.activityTime(b) - this.activityTime(a));
  }

  private activityTime(chat: ListChatItem): number {
    const ts = chat.updated_at || chat.created_at;
    const parsed = ts ? Date.parse(ts) : NaN;
    return isNaN(parsed) ? 0 : parsed;
  }

  onCloseDrawer() {
    this.closeDrawer.emit();
  }

  getOrgName(orgId: string): Observable<string> {
    return this.orgsService.getOrgNameById(orgId);
  }

  getPositiveFeedbackCount(chat: ListChatItemWithFeedback): number {
    // ChatFeedback.value: thumbs-up (1) vs thumbs-down (0).
    return chat.feedback?.filter((f) => f.value === 1).length || 0;
  }

  getNegativeFeedbackCount(chat: ListChatItemWithFeedback): number {
    return chat.feedback?.filter((f) => f.value === 0).length || 0;
  }

  getPositiveFeedbackComments(chat: ListChatItemWithFeedback): string[] {
    if (!chat.feedback) return [];

    const comments = chat.feedback
      .filter((f) => f.value === 1 && f.comment && f.comment.trim().length > 0)
      .map((f) => f.comment!.trim());

    // Return unique comments only
    return [...new Set(comments)];
  }

  getNegativeFeedbackComments(chat: ListChatItemWithFeedback): string[] {
    if (!chat.feedback) return [];

    const comments = chat.feedback
      .filter((f) => f.value === 0 && f.comment && f.comment.trim().length > 0)
      .map((f) => f.comment!.trim());

    // Return unique comments only
    return [...new Set(comments)];
  }

  getPositiveFeedbackTooltip(chat: ListChatItemWithFeedback): string {
    const count = this.getPositiveFeedbackCount(chat);
    const comments = this.getPositiveFeedbackComments(chat);

    if (comments.length === 0) {
      return `${count} positive feedback`;
    }

    return `${count} positive feedback:\n\n${comments.map((c, i) => `${i + 1}. ${c}`).join('\n')}`;
  }

  getNegativeFeedbackTooltip(chat: ListChatItemWithFeedback): string {
    const count = this.getNegativeFeedbackCount(chat);
    const comments = this.getNegativeFeedbackComments(chat);

    if (comments.length === 0) {
      return `${count} negative feedback`;
    }

    return `${count} negative feedback:\n\n${comments.map((c, i) => `${i + 1}. ${c}`).join('\n')}`;
  }
}
